/**
 * The reseller's own partner ledger (TMMRSL-0080, TMMAPI-0162 U3/U5). Records are the generated
 * contract (re-vendored at U5). Own payee only: no `{payeeRef}` in the path, bearer only.
 *
 * Required money is the number tmm-api sent. Optional fields stay absent when unset.
 * An unknown hold kind is `OTHER` with the raw kind kept. An unknown statement status is kept
 * as sent. `reason` on a hold is part of the record (the product admin shows it); this console
 * does not read it beyond passing it through.
 */
import type {
  LedgerHoldView,
  LedgerLinePage,
  LedgerLineView,
  LedgerStatementPage,
  LedgerStatementView,
  PartnerLedgerView,
} from './generated';
import { request } from './client';
import { ApiError, backendCode } from './errors';
import { asOptionalString } from './resellerAdmin';

export type {
  LedgerHoldView,
  LedgerLinePage,
  LedgerLineView,
  LedgerStatementPage,
  LedgerStatementView,
  PartnerLedgerView,
};

const LEDGER = '/v1/reseller-admin/ledger';

export const LEDGER_HOLD_KINDS = [
  'FOR_CAUSE',
  'REVIEW',
  'TAX_ID_UNVERIFIED',
  'PAYOUT_RETURNED',
  'COLLECTION_CONFLICT',
  'OTHER',
] as const;

type LedgerHoldKind = (typeof LEDGER_HOLD_KINDS)[number];

function present<T extends string | number | boolean>(value: T | undefined): value is T {
  return value !== undefined;
}

function normalizeHold(raw: LedgerHoldView): LedgerHoldView {
  const sent = raw.kind;
  const known = (LEDGER_HOLD_KINDS as readonly string[]).includes(sent);
  const kind = (known ? sent : 'OTHER') as LedgerHoldKind;
  const rawKind =
    kind === 'OTHER'
      ? (asOptionalString(raw.rawKind) ?? (sent !== '' && sent !== 'OTHER' ? sent : undefined))
      : undefined;
  const reason = asOptionalString(raw.reason);
  const releasedAt = asOptionalString(raw.releasedAt);
  return {
    holdId: raw.holdId,
    kind,
    ...(rawKind !== undefined ? { rawKind } : {}),
    amountMinor: raw.amountMinor,
    overCap: raw.overCap,
    ...(reason !== undefined ? { reason } : {}),
    openedAt: raw.openedAt,
    ...(releasedAt !== undefined ? { releasedAt } : {}),
  };
}

export function normalizeStatement(body: unknown): LedgerStatementView {
  const raw = body as LedgerStatementView;
  return {
    statementId: raw.statementId,
    periodMonth: raw.periodMonth,
    currency: raw.currency,
    status: raw.status,
    openingMinor: raw.openingMinor,
    linesMinor: raw.linesMinor,
    closingMinor: raw.closingMinor,
    ...(present(raw.lineCount) ? { lineCount: raw.lineCount } : {}),
    ...(asOptionalString(raw.closedAt) !== undefined ? { closedAt: raw.closedAt } : {}),
    ...(asOptionalString(raw.paidOn) !== undefined ? { paidOn: raw.paidOn } : {}),
    ...(present(raw.paidMinor) ? { paidMinor: raw.paidMinor } : {}),
    ...(asOptionalString(raw.externalReference) !== undefined
      ? { externalReference: raw.externalReference }
      : {}),
  };
}

export function normalizePartnerLedger(body: unknown): PartnerLedgerView {
  const raw = body as PartnerLedgerView;
  return {
    payeeRef: raw.payeeRef,
    currency: raw.currency,
    balanceMinor: raw.balanceMinor,
    openHolds: raw.openHolds.map(normalizeHold),
    lastStatement: raw.lastStatement === null ? null : normalizeStatement(raw.lastStatement),
    payoutActionsRetired: raw.payoutActionsRetired,
  };
}

/** The cut-over signal, alone. Anything other than boolean `true` is not retired. */
export function payoutActionsRetired(view: PartnerLedgerView | null | undefined): boolean {
  return view?.payoutActionsRetired === true;
}

export function normalizeStatementPage(body: unknown): LedgerStatementPage {
  const raw = body as LedgerStatementPage;
  return {
    statements: raw.statements.map(normalizeStatement),
    hasMore: raw.hasMore,
    ...(asOptionalString(raw.nextAfterId) !== undefined ? { nextAfterId: raw.nextAfterId } : {}),
  };
}

function normalizeLine(raw: LedgerLineView): LedgerLineView {
  const periodMonth = asOptionalString(raw.periodMonth);
  const description = asOptionalString(raw.description);
  const reason = asOptionalString(raw.reason);
  const reversesLineId = asOptionalString(raw.reversesLineId);
  const statementId = asOptionalString(raw.statementId);
  return {
    lineId: raw.lineId,
    kind: raw.kind,
    amountMinor: raw.amountMinor,
    currency: raw.currency,
    ...(periodMonth !== undefined ? { periodMonth } : {}),
    createdAt: raw.createdAt,
    ...(description !== undefined ? { description } : {}),
    ...(reason !== undefined ? { reason } : {}),
    ...(reversesLineId !== undefined ? { reversesLineId } : {}),
    ...(statementId !== undefined ? { statementId } : {}),
    ...(raw.voided === true ? { voided: true } : {}),
  };
}

export function normalizeLinePage(body: unknown): LedgerLinePage {
  const raw = body as LedgerLinePage;
  return {
    lines: raw.lines.map(normalizeLine),
    hasMore: raw.hasMore,
    ...(asOptionalString(raw.nextAfterId) !== undefined ? { nextAfterId: raw.nextAfterId } : {}),
  };
}

/** A non-zero balance, an open hold, or a last statement: the ledger is in use. */
export function ledgerInUse(view: PartnerLedgerView): boolean {
  return view.balanceMinor !== 0 || view.openHolds.length > 0 || view.lastStatement != null;
}

function notFoundCode(err: unknown): string | undefined {
  return err instanceof ApiError && err.status === 404 ? backendCode(err) : undefined;
}

/** The reseller has no payee yet. The same screen as an empty ledger, not an error. */
export function isPayeeNotFound(err: unknown): boolean {
  return notFoundCode(err) === 'PAYEE_NOT_FOUND';
}

/** The statement in the URL is gone. Clear `statement` and show a short note. */
export function isStatementNotFound(err: unknown): boolean {
  return notFoundCode(err) === 'STATEMENT_NOT_FOUND';
}

/**
 * A 404 with any other code, or none: the route is not deployed yet. A note, never an error page.
 * `PAYEE_NOT_FOUND` and `STATEMENT_NOT_FOUND` are the only coded 404s this console treats otherwise.
 */
export function isLedgerNotDeployed(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const code = backendCode(err);
  return code !== 'PAYEE_NOT_FOUND' && code !== 'STATEMENT_NOT_FOUND';
}

function afterQuery(afterId?: string, extra?: Record<string, string>) {
  const query: Record<string, string> = { ...extra };
  if (afterId) query.afterId = afterId;
  return Object.keys(query).length > 0 ? { query } : {};
}

function statementPath(statementId: string): string {
  return `${LEDGER}/statements/${encodeURIComponent(statementId)}`;
}

export const ledgerApi = {
  getLedger(): Promise<PartnerLedgerView> {
    return request<unknown>('GET', LEDGER).then(normalizePartnerLedger);
  },

  listStatements(afterId?: string): Promise<LedgerStatementPage> {
    return request<unknown>('GET', `${LEDGER}/statements`, afterQuery(afterId)).then(normalizeStatementPage);
  },

  getStatement(statementId: string): Promise<LedgerStatementView> {
    return request<unknown>('GET', statementPath(statementId)).then(normalizeStatement);
  },

  listStatementLines(statementId: string, afterId?: string): Promise<LedgerLinePage> {
    return request<unknown>('GET', `${statementPath(statementId)}/lines`, afterQuery(afterId)).then(
      normalizeLinePage,
    );
  },

  listUnassignedLines(afterId?: string): Promise<LedgerLinePage> {
    return request<unknown>('GET', `${LEDGER}/lines`, afterQuery(afterId, { unassigned: 'true' })).then(
      normalizeLinePage,
    );
  },
};
